export interface BreadcrumbConfig {
  label: string
  dynamic?: {
    param: string
    resolver: (value: string) => string
  }
}

export interface RouteConfig {
  path: string
  breadcrumbs: BreadcrumbConfig[]
}

// Exception type mappings - pre-API-load fallback only (see exceptionCode
// resolver below). Sales codes are intentionally absent: DynamicExceptionRoute
// always overrides via API ``name_short``, and a hardcoded entry would just
// drift from the DB. Product/system entries are kept pending the same audit.
const exceptionDisplayNames: Record<string, string> = {
  // Product exceptions
  'margin-check-avg-cost': 'Margin check (avg cost)',
  'ls-product-no-category': 'Products without category',
  'xero-account-set': 'Xero account set',
  'ls-product-negative-inventory': 'Negative inventory',

  // System exceptions
  'registers-not-closed': 'Registers not closed',
}

// Tool display names
const toolDisplayNames: Record<string, string> = {
  'duplicate-sales': 'Duplicate Sales Checker',
}

// Resolver functions for dynamic segments
export const resolvers = {
  // Returns '' for codes not in the static map so the breadcrumb framework
  // skips the leaf until DynamicExceptionRoute supplies the API name_short
  // via dynamicCrumbOverride. Avoids flashing the title-cased slug.
  exceptionCode: (code: string): string => {
    return exceptionDisplayNames[code] ?? ''
  },

  toolName: (name: string): string => {
    return toolDisplayNames[name] || name
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  },
}

// Inspector route configurations with breadcrumb metadata
export const inspectorRoutes: RouteConfig[] = [
  // Dashboard
  {
    path: '/inspect/:fp_code',
    breadcrumbs: [
      { label: 'Dashboard' }
    ]
  },
  
  // Health Check
  {
    path: '/inspect/:fp_code/health',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Health Check' }
    ]
  },

  // Exceptions routes (new structure)
  {
    path: '/inspect/:fp_code/exceptions',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' }
    ]
  },
  {
    path: '/inspect/:fp_code/exceptions/sales',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' },
      { label: 'Sales' }
    ]
  },
  {
    path: '/inspect/:fp_code/exceptions/sales/:exception_code',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' },
      { label: 'Sales' },
      {
        label: '',
        dynamic: {
          param: 'exception_code',
          resolver: resolvers.exceptionCode
        }
      }
    ]
  },
  {
    path: '/inspect/:fp_code/exceptions/stock-management',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' },
      { label: 'Stock Management' }
    ]
  },
  {
    path: '/inspect/:fp_code/exceptions/stock-management/:exception_code',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' },
      { label: 'Stock Management' },
      {
        label: '',
        dynamic: {
          param: 'exception_code',
          resolver: resolvers.exceptionCode
        }
      }
    ]
  },
  {
    path: '/inspect/:fp_code/exceptions/product-setup',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' },
      { label: 'Product Setup' }
    ]
  },
  {
    path: '/inspect/:fp_code/exceptions/product-setup/:exception_code',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' },
      { label: 'Product Setup' },
      {
        label: '',
        dynamic: {
          param: 'exception_code',
          resolver: resolvers.exceptionCode
        }
      }
    ]
  },
  {
    path: '/inspect/:fp_code/exceptions/product-pricing',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' },
      { label: 'Product Pricing' }
    ]
  },
  {
    path: '/inspect/:fp_code/exceptions/product-pricing/:exception_code',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' },
      { label: 'Product Pricing' },
      {
        label: '',
        dynamic: {
          param: 'exception_code',
          resolver: resolvers.exceptionCode
        }
      }
    ]
  },
  {
    path: '/inspect/:fp_code/exceptions/systems',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' },
      { label: 'Systems' }
    ]
  },
  {
    path: '/inspect/:fp_code/exceptions/systems/:exception_code',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' },
      { label: 'Systems' },
      {
        label: '',
        dynamic: {
          param: 'exception_code',
          resolver: resolvers.exceptionCode
        }
      }
    ]
  },
  {
    path: '/inspect/:fp_code/exceptions/client',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' },
      { label: 'Client' }
    ]
  },
  {
    path: '/inspect/:fp_code/exceptions/client/:exception_code',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' },
      { label: 'Client' },
      {
        label: '',
        dynamic: {
          param: 'exception_code',
          resolver: resolvers.exceptionCode
        }
      }
    ]
  },
  {
    path: '/inspect/:fp_code/exceptions/accounting',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' },
      { label: 'Accounting' }
    ]
  },
  {
    path: '/inspect/:fp_code/exceptions/accounting/:exception_code',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Exceptions' },
      { label: 'Accounting' },
      {
        label: '',
        dynamic: {
          param: 'exception_code',
          resolver: resolvers.exceptionCode
        }
      }
    ]
  },

  // Sales routes - OLD: Replaced by new modular structure
  /*
  {
    path: '/inspect/:fp_code/sales',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Sales' }
    ]
  },
  {
    path: '/inspect/:fp_code/sales/list',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Sales' }
    ]
  },
  {
    path: '/inspect/:fp_code/sales/exceptions',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Sales' },
      { label: 'Exceptions' }
    ]
  },
  {
    path: '/inspect/:fp_code/sales/exceptions/:exception_code',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Sales' },
      { label: 'Exceptions' },
      { 
        label: '',
        dynamic: {
          param: 'exception_code',
          resolver: resolvers.exceptionCode
        }
      }
    ]
  },
  {
    path: '/inspect/:fp_code/sales/reports',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Sales' },
      { label: 'Reports' }
    ]
  },
  {
    path: '/inspect/:fp_code/sales/tools',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Sales' },
      { label: 'Tools' }
    ]
  },
  {
    path: '/inspect/:fp_code/sales/tools/:tool_name',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Sales' },
      { label: 'Tools' },
      {
        label: '',
        dynamic: {
          param: 'tool_name',
          resolver: resolvers.toolName
        }
      }
    ]
  },
  */
  
  // Inventory routes - OLD: Replaced by new modular structure
  /*
  {
    path: '/inspect/:fp_code/inventory',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Inventory' }
    ]
  },
  {
    path: '/inspect/:fp_code/inventory/list',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Inventory' }
    ]
  },
  {
    path: '/inspect/:fp_code/inventory/exceptions',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Inventory' },
      { label: 'Exceptions' }
    ]
  },
  {
    path: '/inspect/:fp_code/inventory/exceptions/:exception_code',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Inventory' },
      { label: 'Exceptions' },
      {
        label: '',
        dynamic: {
          param: 'exception_code',
          resolver: resolvers.exceptionCode
        }
      }
    ]
  },
  {
    path: '/inspect/:fp_code/inventory/reports',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Inventory' },
      { label: 'Reports' }
    ]
  },
  {
    path: '/inspect/:fp_code/inventory/tools',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Inventory' },
      { label: 'Tools' }
    ]
  },
  {
    path: '/inspect/:fp_code/inventory/tools/:tool_name',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Inventory' },
      { label: 'Tools' },
      {
        label: '',
        dynamic: {
          param: 'tool_name',
          resolver: resolvers.toolName
        }
      }
    ]
  },
  {
    path: '/inspect/:fp_code/inventory/inventory',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Inventory' },
      { label: 'Inventory' }
    ]
  },
  */
  
  // Clients routes
  {
    path: '/inspect/:fp_code/clients',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Clients' }
    ]
  },
  
  // Accounts routes
  {
    path: '/inspect/:fp_code/accounts',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Accounts' }
    ]
  },
  
  // Systems routes - OLD: Replaced by Support hub
  /*
  {
    path: '/inspect/:fp_code/systems',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Systems' }
    ]
  },
  {
    path: '/inspect/:fp_code/systems/list',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Systems' }
    ]
  },
  {
    path: '/inspect/:fp_code/systems/exceptions',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Systems' },
      { label: 'Exceptions' }
    ]
  },
  {
    path: '/inspect/:fp_code/systems/exceptions/:exception_code',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Systems' },
      { label: 'Exceptions' },
      {
        label: '',
        dynamic: {
          param: 'exception_code',
          resolver: resolvers.exceptionCode
        }
      }
    ]
  },
  {
    path: '/inspect/:fp_code/systems/tools',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Systems' },
      { label: 'Tools' }
    ]
  },
  {
    path: '/inspect/:fp_code/systems/tools/:tool_name',
    breadcrumbs: [
      { label: 'Dashboard' },
      { label: 'Systems' },
      { label: 'Tools' },
      {
        label: '',
        dynamic: {
          param: 'tool_name',
          resolver: resolvers.toolName
        }
      }
    ]
  },
  */
]

// Helper function to find matching route config
export const findRouteConfig = (pathname: string): RouteConfig | null => {
  // Sort routes by specificity (more segments = more specific)
  const sortedRoutes = [...inspectorRoutes].sort((a, b) => {
    const aSegments = a.path.split('/').length
    const bSegments = b.path.split('/').length
    return bSegments - aSegments
  })
  
  for (const route of sortedRoutes) {
    // Convert route pattern to regex
    const pattern = route.path
      .replace(/:[^/]+/g, '([^/]+)') // Replace :param with capture group
      .replace(/\//g, '\\/') // Escape forward slashes
    
    const regex = new RegExp(`^${pattern}$`)
    if (regex.test(pathname)) {
      return route
    }
  }
  
  return null
}